import { css } from '@emotion/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material'
import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { match } from 'ts-pattern'
import { z, ZodError } from 'zod'
import { makeZodI18nMap } from 'zod-i18n-map'
import {
  CloseAbsoluteDiv,
  CloseIconContainer,
  ErrorMessageContainer,
  ModalBody
} from 'components/Auth/AuthModal.style'
import { Button, ButtonVariant } from 'components/common/Button'
import { LogoButton } from 'components/common/Button/LogoButton'
import { Loader } from 'components/common/Loader'
import { InputErrorLabel, TextInput } from 'components/common/TextInput'
import { NewFlexContainer } from 'components/common/common.style'
import {
  WaitingListAttendee,
  WaitingListAttendeeSchema
} from 'domain/models/WaitingLists/WaitingListsRace'
import { apiClient } from 'services/api/client'
import { LiveEventRace } from 'services/api/database/EventService'
import { useGetAttendeeProfile } from 'services/attendees/useGetAttendeeProfile'
import { media } from 'styles/responsive'

interface RegistrationFormProps {
  open: boolean
  onClose: () => void
  race: LiveEventRace
  confirmRegistration: (attendee: WaitingListAttendee) => void
}

const RegistrationForm = ({
  open,
  onClose,
  race,
  confirmRegistration
}: RegistrationFormProps) => {
  const { t } = useTranslation('checkout')
  const [isLoading, setIsLoading] = useState(false)
  const { locale } = useRouter()
  const { t: tRoot } = useTranslation()
  const isRelay = race.raceSegments.length > 0

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    watch
  } = useForm<WaitingListAttendee>({
    resolver: zodResolver(WaitingListAttendeeSchema(isRelay))
  })

  z.setErrorMap(makeZodI18nMap({ t: tRoot }))
  const [createAttendeeError, setCreateAttendeeError] = useState('')
  const { data: attendeeProfileFromUser } = useGetAttendeeProfile(open)

  useEffect(() => {
    if (attendeeProfileFromUser?.data) {
      const { firstName, lastName, email } = attendeeProfileFromUser.data
      reset({ firstName, lastName, email })
    }
  }, [attendeeProfileFromUser?.data])

  const handleClose = () => {
    onClose()
    setCreateAttendeeError('')
    clearErrors()
    reset()
  }

  const attendee = watch()

  const onSubmit = async (data: WaitingListAttendee) => {
    try {
      setCreateAttendeeError('')
      setIsLoading(true)
      await apiClient.post('/waiting-lists/add-attendee', {
        waitingListAttendee: data,
        raceId: race.id,
        language: locale,
        userId:
          data.email === attendeeProfileFromUser?.data?.email
            ? attendeeProfileFromUser.data.userId
            : undefined
      })
      confirmRegistration(attendee)
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data && 'error' in error.response.data) {
          if (typeof error.response.data.error === 'string') {
            match(error.response.data.error)
              .with('ALREADY_ASSIGNED', (errorKey) => {
                setCreateAttendeeError(t(`race.waitingList.error.${errorKey}`))
              })
              .otherwise((errorMessage) => {
                setCreateAttendeeError(errorMessage)
              })
          } else {
            const zodError = error.response.data.error as ZodError
            setCreateAttendeeError(
              `${zodError.issues[0]?.path?.[0]?.toString() ?? ''}: ${
                zodError.issues[0]?.message ?? ''
              }`
            )
          }
        } else {
          setCreateAttendeeError(error.message)
        }
      } else {
        setCreateAttendeeError(t('attendeeProfile.snackbar.error.message'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle variant="h4">{t('race.waitingList.title')}</DialogTitle>
      <CloseAbsoluteDiv>
        <LogoButton
          onClick={handleClose}
          icon={<CloseIconContainer />}
          noBorder
        />
      </CloseAbsoluteDiv>
      <DialogContent dividers sx={{ p: 1 }}>
        <ModalBody
          css={css`
            gap: 0;
            ${media.maxWidth.sm} {
              padding: 8px;
            }
          `}
        >
          <Stack spacing={2}>
            <Typography variant="body2">
              <Trans
                i18nKey="race.waitingList.form.description"
                ns="checkout"
                values={{ race: race.name }}
                components={{ strong: <strong /> }}
              />
            </Typography>
            <NewFlexContainer>
              {race.raceSegments.length > 0 && (
                <TextInput
                  inputLabel={t('relay.team.title')}
                  errorMessage={errors.relayTeamName?.message}
                  inputProps={{
                    ...register('relayTeamName'),
                    style: { textTransform: 'capitalize' }
                  }}
                  fullSize
                  lowercase
                  noMargin
                />
              )}
            </NewFlexContainer>
            <NewFlexContainer
              gap="16px"
              css={css`
                ${media.maxWidth.sm} {
                  gap: 8px;
                }
              `}
            >
              <TextInput
                fullSize
                inputLabel={t('attendee.formFields.firstName')}
                errorMessage={errors?.firstName?.message}
                inputProps={{
                  ...register('firstName'),
                  style: { textTransform: 'capitalize' }
                }}
                lowercase
                noMargin
              />
              <TextInput
                fullSize
                inputLabel={t('attendee.formFields.lastName')}
                errorMessage={errors?.lastName?.message}
                inputProps={{
                  ...register('lastName'),
                  style: { textTransform: 'capitalize' }
                }}
                lowercase
                noMargin
              />
            </NewFlexContainer>
            <TextInput
              fullSize
              inputLabel={t('attendee.formFields.email')}
              errorMessage={errors?.email?.message}
              inputProps={{ ...register('email') }}
              lowercase
              noMargin
            />
          </Stack>
          <ErrorMessageContainer>
            {createAttendeeError && (
              <InputErrorLabel>{createAttendeeError}</InputErrorLabel>
            )}
          </ErrorMessageContainer>
        </ModalBody>
      </DialogContent>
      <DialogActions
        sx={{ p: 2, padding: '16px 20px' }}
        css={css`
          ${media.maxWidth.sm} {
            padding: 16px 8px;
          }
        `}
      >
        <NewFlexContainer flexDirection="column" width="100%" padding="0 8px">
          <Button
            label={t('race.waitingList.button.register')}
            fullWidth
            variant={ButtonVariant.Primary}
            type="submit"
            icon={isLoading ? <Loader /> : undefined}
            disabled={isLoading}
          />
        </NewFlexContainer>
      </DialogActions>
    </form>
  )
}

export default RegistrationForm
